import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { StorageKeys } from '@/lib/localStorage';
import type { ProgressEvent } from '@shared/schemas';
import type {
  Progress,
  TicketProgress,
  TicketStatus,
  HomeworkStatus,
  HomeworkProgress,
  QuickCheckProgress,
  SprintProgress,
  QuizProgress,
  MockResult,
  FlashcardState,
  AchievementsState,
  CapstoneState,
  CapstoneArtifactId,
  CapstoneRubricRequiredId,
  CapstoneRubricBonusId,
  AccentOverlay,
  EarnedBadge,
  WizardBlockState,
  InlineQcState,
} from '@/types';
import { defaultAchievements, defaultBlock1Capstone, defaultProgress } from './defaults';

interface ProgressContextValue {
  progress: Progress;
  setLast: (sprintId: string, ticketId?: string) => void;
  setTicketStatus: (ticketId: string, status: TicketStatus) => void;
  setHomeworkStatus: (homeworkId: string, status: HomeworkStatus) => void;
  /** Homework v2 · patch selfCheck/submission without clobbering status. */
  patchHomework: (homeworkId: string, patch: Partial<HomeworkProgress>) => void;
  setACChecked: (ticketId: string, acId: string, done: boolean) => void;
  setQuickCheck: (ticketId: string, qc: QuickCheckProgress) => void;
  setNote: (ticketId: string, note: string) => void;
  /** Phase 7 · persist active wizard step inside ticket content. */
  setWizardStep: (ticketId: string, wizardId: string, step: number) => void;
  /** Phase 7 · persist inline QC selection + reveal state inside ticket content. */
  setInlineQc: (ticketId: string, blockId: string, state: InlineQcState) => void;
  setSprintProgress: (sprintId: string, patch: Partial<SprintProgress>) => void;
  setSprintReflection: (sprintId: string, text: string) => void;
  /** Slot QUIZ_AGGREGATION_WIDGET · persist Progress.quiz[bankId] after each attempt submit. */
  setQuizProgress: (bankId: string, quizProgress: QuizProgress) => void;
  /** CHAT_2B Q-3 · set/clear blocked reason on ticket (max 200 chars · UI overflow guard). */
  setTicketBlockedReason: (ticketId: string, reason: string | undefined) => void;
  /** CHAT_2B Q-5 · set multi-assignee array (UUID strings · assignees[0] = primary). */
  setTicketAssignees: (ticketId: string, assignees: string[]) => void;
  /** CHAT_2B Q-2 · append event to progress.events stream (auto-prunes >90d entries per ADR-009). */
  addProgressEvent: (event: Omit<ProgressEvent, 'id' | 'at'>) => void;
  markWelcomeSeen: () => void;
  markSprint0TourCompleted: () => void;
  setExamDate: (iso: string | undefined) => void;
  setMockResult: (slot: 'mock1' | 'mock2', result: MockResult) => void;
  /** Persist the Block-1 «Перевал» exam result (bank `block1-exam`) · slot BLOCK1_EXAM. */
  setBlockExamResult: (result: MockResult) => void;
  setFlashcardState: (termId: string, state: FlashcardState) => void;
  getTicket: (ticketId: string) => TicketProgress | undefined;
  recordEarnedBadge: (badge: EarnedBadge) => void;
  setAchievementProgress: (badgeId: string, value: number) => void;
  setAccentOverlay: (overlay: AccentOverlay) => void;
  unlockAccentOverlay: (overlay: AccentOverlay) => void;
  unlockMiniLesson: (lessonId: string) => void;
  markMiniLessonRead: (lessonId: string) => void;
  setMiniLessonReadingProgress: (lessonId: string, pct: number) => void;
  recordQuizPackResult: (packId: string, scorePct: number, perQuestion: Record<string, boolean>) => void;
  recordPomodoroSession: (startHour: number) => void;
  /**
   * Хаб «Твоя практика» · накопить секунды экранной активности в
   * achievements.activityByDay[сегодня]. С ticketId (доска · 11 JUL 2026)
   * дополнительно копит activityByTicket[ticketId] — реальное время на тикете.
   */
  addScreenTime: (seconds: number, ticketId?: string | null) => void;
  /**
   * Экзамены-вехи · выставить capability-флаги после сдачи (checkpoint_exams · 25 JUN).
   * Питает бейджи «взят / налегке / все рубежи / с первой попытки / работа над ошибками».
   */
  recordExamAchievementFlags: (input: {
    bankId: string;
    pct: number;
    passed: boolean;
    attempt: number;
  }) => void;
  recordThemeUsage: (theme: string) => void;
  recordLocaleUsage: (locale: string) => void;
  markSeniorExtensionOpened: (key: string) => void;
  ensureBlock1Capstone: () => CapstoneState;
  setCapstoneArtifactSubmitted: (artifactId: CapstoneArtifactId, submitted: boolean) => void;
  setCapstoneRubricRequired: (id: CapstoneRubricRequiredId, value: boolean) => void;
  setCapstoneRubricBonus: (id: CapstoneRubricBonusId, value: boolean) => void;
  markCapstoneReleaseUnlocked: () => void;
  /**
   * Bulk-replace local progress with a server snapshot · FS slot.
   *
   * Used by the `useProgressSync` glue on (a) initial GET /progress after
   * auth, (b) 412 conflict refetch (LWW per-branch already applied
   * server-side). The new state is also written back into localStorage by
   * the underlying `useLocalStorage` setter — keeps the hydrate-cache fresh.
   */
  applyServerState: (next: Progress) => void;
  /**
   * Reset local progress to factory defaults · slot slot_logout_reset
   * (UAT-FINDING-CRIT). Writes `defaultProgress()` through `setProgress`, so the
   * localStorage hydrate-cache (ADR-006) is overwritten with defaults too — a
   * stale snapshot can no longer survive into a subsequent mount. Renamed from
   * the earlier `reset` (which had no consumers) to make the cross-user intent
   * explicit. Note: on logout the leak is closed at the localStorage layer in
   * `AuthContext` (ProgressProvider unmounts inside AuthGate), so this is the
   * in-memory counterpart used for tests and any future code path that keeps
   * the provider mounted across an identity switch.
   */
  resetProgress: () => void;
  /**
   * Read-only DEMO mode · slot_progression_plan_quiz Phase 3 (2 JUN 2026).
   * When true, EVERY progress write is dropped (no state change, no localStorage,
   * no server sync) so a trial user can «заглянуть» into a locked sprint without
   * persisting anything. Toggled by App.tsx on enter/exit of a demo sprint view.
   */
  demoMode: boolean;
  setDemoMode: (on: boolean) => void;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

const ensureTicket = (
  state: Progress,
  ticketId: string,
  patch: Partial<TicketProgress>,
): Progress => {
  const existing = state.tickets[ticketId] ?? {
    status: 'todo' as TicketStatus,
    acChecked: {},
  };
  // CHAT_2B Q-6 · auto-bump updatedAt on ANY ticket mutation (status · ac toggle
  // · note · blockedReason · assignees · etc.) · powers «обновлён 14 мин назад»
  // meta and ADR-007 If-Unmodified-Since conflict detection. Skip override if
  // caller explicitly set updatedAt (e.g., from server sync push).
  const updatedAt = patch.updatedAt ?? new Date().toISOString();
  return {
    ...state,
    tickets: {
      ...state.tickets,
      [ticketId]: { ...existing, ...patch, updatedAt },
    },
  };
};

/**
 * Hydrate any state shape predating the achievements/capstone fields.
 * Old saved Progress (Phase 1-3) lacked these branches; we add them on read.
 */
const hydrateProgress = (p: Progress): Progress => {
  // Boot guard (13 JUN 2026): a corrupt or old-shape save (null, primitive,
  // array — valid JSON but not a Progress object) must not crash the app on load
  // by throwing on `p.achievements`. Fall back to a fresh default instead. The
  // RootBoundary is the outer net; this fixes the most common case at the source.
  if (p === null || typeof p !== 'object' || Array.isArray(p)) {
    return defaultProgress();
  }
  let next = p;
  const defaults = defaultAchievements();
  if (!p.achievements) {
    next = { ...next, achievements: defaults };
  } else {
    const a = p.achievements as Partial<AchievementsState>;
    next = {
      ...next,
      achievements: {
        ...defaults,
        ...a,
        // Deep-merge nested branches so older saves missing newer fields are filled.
        miniLessons: { ...defaults.miniLessons, ...(a.miniLessons ?? {}) },
        buddySkin: { ...defaults.buddySkin, ...(a.buddySkin ?? {}) },
        accentOverlay: { ...defaults.accentOverlay, ...(a.accentOverlay ?? {}) },
      } as AchievementsState,
    };
  }
  if (!p.capstone) {
    next = { ...next, capstone: {} };
  }
  // Slot UI_C4 · 2026-05-03 · backfill homework branch для старых saves (pre-Backlog).
  if (!p.homework) {
    next = { ...next, homework: {} };
  }
  // NB: NO onboarding backfill here. The «Первые шаги» tutorial fields (Ф1b) are
  // optional; onboarding-state's normalize() coerces missing keys → false, and the
  // first dismiss persists them via patchOnboarding. Rebuilding onboarding here made a
  // NEW reference every hydrate for migrating accounts (keys absent in storedProgress,
  // rebuild never written back) → useProgressSync ref-compare re-PATCHed onboarding on
  // every change → render/sync loop (page flicker · Studio Map re-popping). 19 JUN.
  return next;
};

/**
 * Append a rich progress event with a fresh id/timestamp, pruning events older
 * than 90 days on write (ADR-009 · matches pomodoroSessions retention). PURE, so
 * it's safe to call inside a `setProgress` updater — hooked setters emit their
 * event in the SAME state update (no extra render). Powers the «Обзор» activity
 * feed + charts once the event-log is turned on (3 JUL 2026 · HANDOFF_board_overview P3).
 */
const EVENT_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
function appendEvent(prev: Progress, kind: string, payload: Record<string, unknown>): Progress {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const at = new Date().toISOString();
  const cutoff = Date.now() - EVENT_RETENTION_MS;
  const existing = prev.events ?? [];
  const pruned = existing.filter((e) => {
    const t = Date.parse(e.at);
    return Number.isFinite(t) && t >= cutoff;
  });
  return { ...prev, events: [...pruned, { id, at, kind, payload }] };
}

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [storedProgress, persistProgress] = useLocalStorage<Progress>(
    StorageKeys.progress,
    defaultProgress(),
  );
  // Read-only DEMO gate (Phase 3): when on, drop every write at the single choke
  // point so all 20+ setters become no-ops without touching each one. A ref backs
  // the guard so the wrapper stays referentially stable (setters depend on it).
  const demoModeRef = useRef(false);
  const [demoMode, setDemoModeState] = useState(false);
  const setDemoMode = useCallback((on: boolean) => {
    demoModeRef.current = on;
    setDemoModeState(on);
  }, []);
  const setProgress = useCallback<typeof persistProgress>(
    (updater) => {
      if (demoModeRef.current) return; // read-only demo · drop all writes
      persistProgress(updater);
    },
    [persistProgress],
  );
  const progress = useMemo(() => hydrateProgress(storedProgress), [storedProgress]);
  const hydratedOnce = useRef(false);
  useEffect(() => {
    if (hydratedOnce.current) return;
    hydratedOnce.current = true;
    if (!storedProgress.achievements || !storedProgress.capstone) {
      setProgress(progress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLast = useCallback(
    (sprintId: string, ticketId?: string) => {
      setProgress((prev) => ({
        ...prev,
        last: { sprintId, ticketId, timestamp: new Date().toISOString() },
      }));
    },
    [setProgress],
  );

  const setTicketStatus = useCallback(
    (ticketId: string, status: TicketStatus) => {
      setProgress((prev) => {
        const existing = prev.tickets[ticketId];
        const startedAt = existing?.startedAt ?? new Date().toISOString();
        const completedAt = status === 'done' ? new Date().toISOString() : existing?.completedAt;
        // CHAT_2B Q-4 · bump columnChangedAt только when status actually changes
        // (not on idempotent re-setStatus calls) · powers Board age-in-column meta.
        const columnChangedAt =
          existing?.status !== status ? new Date().toISOString() : existing?.columnChangedAt;
        const next = ensureTicket(prev, ticketId, { status, startedAt, completedAt, columnChangedAt });
        // event-log (3 JUL · P3.1): emit only on a REAL transition into done/in-progress
        // (not idempotent re-sets), so the «Обзор» feed/charts count each move once.
        if (existing?.status !== status && (status === 'done' || status === 'in-progress')) {
          return appendEvent(next, `ticket.${status}`, { ticketId });
        }
        return next;
      });
    },
    [setProgress],
  );

  const setTicketBlockedReason = useCallback(
    (ticketId: string, reason: string | undefined) => {
      // CHAT_2B Q-3 · trim к max 200 chars per schema cap (zod will reject longer)
      const safe = reason && reason.length > 200 ? reason.slice(0, 200) : reason;
      setProgress((prev) => ensureTicket(prev, ticketId, { blockedReason: safe }));
    },
    [setProgress],
  );

  const setTicketAssignees = useCallback(
    (ticketId: string, assignees: string[]) => {
      // CHAT_2B Q-5 · assignees[0] = primary per ADR-004 (notification routing).
      setProgress((prev) => ensureTicket(prev, ticketId, { assignees }));
    },
    [setProgress],
  );

  const addProgressEvent = useCallback(
    (event: Omit<ProgressEvent, 'id' | 'at'>) => {
      // CHAT_2B Q-2 · Public API · delegates to the shared pure `appendEvent` (id via
      // crypto.randomUUID + timestamp + 90d prune-on-write · ADR-009) so manual calls
      // and setter-hooked events share one code path.
      setProgress((prev) => appendEvent(prev, event.kind, event.payload));
    },
    [setProgress],
  );

  /**
   * Slot UI_C4 · 2026-05-03 · Sprint Backlog homework status setter.
   * Mirror of setTicketStatus pattern · branch `progress.homework[id]` parallel `progress.tickets[id]`.
   */
  const setHomeworkStatus = useCallback(
    (homeworkId: string, status: HomeworkStatus) => {
      setProgress((prev) => {
        const existing = prev.homework?.[homeworkId];
        const startedAt =
          existing?.startedAt ??
          (status === 'in-progress' || status === 'done' ? new Date().toISOString() : undefined);
        const completedAt = status === 'done' ? new Date().toISOString() : existing?.completedAt;
        const next = {
          ...prev,
          homework: {
            ...(prev.homework ?? {}),
            // Preserve v2 fields (selfCheck/submission) on status transitions.
            [homeworkId]: { ...existing, status, startedAt, completedAt },
          },
        };
        // event-log (3 JUL · P3.1): emit once on transition into done.
        if (existing?.status !== 'done' && status === 'done') {
          return appendEvent(next, 'homework.done', { homeworkId });
        }
        return next;
      });
    },
    [setProgress],
  );

  // Homework v2 (slot_homework_rework): patch arbitrary homework fields
  // (selfCheck / submission) without clobbering status/timestamps.
  const patchHomework = useCallback(
    (homeworkId: string, patch: Partial<HomeworkProgress>) => {
      setProgress((prev) => {
        const existing = prev.homework?.[homeworkId] ?? { status: 'locked' as HomeworkStatus };
        return {
          ...prev,
          homework: {
            ...(prev.homework ?? {}),
            [homeworkId]: { ...existing, ...patch },
          },
        };
      });
    },
    [setProgress],
  );

  const setACChecked = useCallback(
    (ticketId: string, acId: string, done: boolean) => {
      setProgress((prev) => {
        const existing = prev.tickets[ticketId];
        const acChecked = { ...(existing?.acChecked ?? {}), [acId]: done };
        return ensureTicket(prev, ticketId, { acChecked });
      });
    },
    [setProgress],
  );

  const setQuickCheck = useCallback(
    (ticketId: string, qc: QuickCheckProgress) => {
      setProgress((prev) => ensureTicket(prev, ticketId, { quickCheck: qc }));
    },
    [setProgress],
  );

  const setNote = useCallback(
    (ticketId: string, note: string) => {
      setProgress((prev) => ensureTicket(prev, ticketId, { note }));
    },
    [setProgress],
  );

  const setWizardStep = useCallback(
    (ticketId: string, wizardId: string, step: number) => {
      setProgress((prev) => {
        const existing = prev.tickets[ticketId];
        const wizardState: Record<string, WizardBlockState> = {
          ...(existing?.wizardState ?? {}),
          [wizardId]: { activeStep: step },
        };
        return ensureTicket(prev, ticketId, { wizardState });
      });
    },
    [setProgress],
  );

  const setInlineQc = useCallback(
    (ticketId: string, blockId: string, state: InlineQcState) => {
      setProgress((prev) => {
        const existing = prev.tickets[ticketId];
        const inlineQc: Record<string, InlineQcState> = {
          ...(existing?.inlineQc ?? {}),
          [blockId]: state,
        };
        return ensureTicket(prev, ticketId, { inlineQc });
      });
    },
    [setProgress],
  );

  const setSprintProgress = useCallback(
    (sprintId: string, patch: Partial<SprintProgress>) => {
      setProgress((prev) => {
        const existing = prev.sprints[sprintId] ?? {
          status: 'available' as const,
          pctDone: 0,
        };
        return {
          ...prev,
          sprints: {
            ...prev.sprints,
            [sprintId]: { ...existing, ...patch },
          },
        };
      });
    },
    [setProgress],
  );

  const setSprintReflection = useCallback(
    (sprintId: string, text: string) => {
      setProgress((prev) => {
        const existing = prev.sprints[sprintId] ?? {
          status: 'active' as const,
          pctDone: 0,
        };
        return {
          ...prev,
          sprints: {
            ...prev.sprints,
            [sprintId]: { ...existing, reflection: text },
          },
        };
      });
    },
    [setProgress],
  );

  const markWelcomeSeen = useCallback(() => {
    setProgress((prev) => ({
      ...prev,
      onboarding: { ...prev.onboarding, welcomeSeen: true },
    }));
  }, [setProgress]);

  const markSprint0TourCompleted = useCallback(() => {
    setProgress((prev) => ({
      ...prev,
      onboarding: { ...prev.onboarding, sprint0TourCompleted: true },
    }));
  }, [setProgress]);

  const setExamDate = useCallback(
    (iso: string | undefined) => {
      setProgress((prev) => ({
        ...prev,
        cockpit: { ...prev.cockpit, examDate: iso },
      }));
    },
    [setProgress],
  );

  const setMockResult = useCallback(
    (slot: 'mock1' | 'mock2', result: MockResult) => {
      setProgress((prev) =>
        appendEvent(
          {
            ...prev,
            cockpit: {
              ...prev.cockpit,
              mockHistory: { ...prev.cockpit.mockHistory, [slot]: result },
            },
          },
          'exam.mock',
          { slot, pct: result.pct },
        ),
      );
    },
    [setProgress],
  );

  const setBlockExamResult = useCallback(
    (result: MockResult) => {
      setProgress((prev) =>
        appendEvent(
          {
            ...prev,
            cockpit: {
              ...prev.cockpit,
              // legacy single slot — последняя сданная веха (S06-диагностика читает его)
              blockExamResult: result,
              // keyed по bankId — каждая веха хранит свой результат (бейджи не затирают друг друга)
              blockExamResults: { ...(prev.cockpit.blockExamResults ?? {}), [result.bankId]: result },
            },
          },
          'exam.block',
          { bankId: result.bankId, pct: result.pct },
        ),
      );
    },
    [setProgress],
  );

  // Slot QUIZ_AGGREGATION_WIDGET 2026-05-12 · per-sprint quiz review state · drives Hybrid gate.
  // Bank id key (e.g. `S01` · `S08`). Mock banks skip persistence per OQ-3=C (bypassGate).
  const setQuizProgress = useCallback(
    (bankId: string, quizProgress: QuizProgress) => {
      setProgress((prev) => ({
        ...prev,
        quiz: { ...(prev.quiz ?? {}), [bankId]: quizProgress },
      }));
    },
    [setProgress],
  );

  const setFlashcardState = useCallback(
    (termId: string, state: FlashcardState) => {
      setProgress((prev) => ({
        ...prev,
        cockpit: {
          ...prev.cockpit,
          flashcards: { ...prev.cockpit.flashcards, [termId]: state },
        },
      }));
    },
    [setProgress],
  );

  const getTicket = useCallback(
    (ticketId: string) => progress.tickets[ticketId],
    [progress.tickets],
  );

  const recordEarnedBadge = useCallback(
    (badge: EarnedBadge) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.earned.some((e) => e.badgeId === badge.badgeId)) return prev;
        const next = { ...prev, achievements: { ...a, earned: [...a.earned, badge] } };
        // event-log (3 JUL · P3.1): emit once per newly-earned badge.
        return appendEvent(next, 'badge.earned', { badgeId: badge.badgeId });
      });
    },
    [setProgress],
  );

  const setAchievementProgress = useCallback(
    (badgeId: string, value: number) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.progress[badgeId] === value) return prev;
        return {
          ...prev,
          achievements: { ...a, progress: { ...a.progress, [badgeId]: value } },
        };
      });
    },
    [setProgress],
  );

  const setAccentOverlay = useCallback(
    (overlay: AccentOverlay) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        const unlocked = a.accentOverlay.unlocked.includes(overlay)
          ? a.accentOverlay.unlocked
          : [...a.accentOverlay.unlocked, overlay];
        return {
          ...prev,
          achievements: {
            ...a,
            accentOverlay: { selected: overlay, unlocked },
          },
        };
      });
    },
    [setProgress],
  );

  const unlockAccentOverlay = useCallback(
    (overlay: AccentOverlay) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.accentOverlay.unlocked.includes(overlay)) return prev;
        return {
          ...prev,
          achievements: {
            ...a,
            accentOverlay: {
              ...a.accentOverlay,
              unlocked: [...a.accentOverlay.unlocked, overlay],
            },
          },
        };
      });
    },
    [setProgress],
  );

  const unlockMiniLesson = useCallback(
    (lessonId: string) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.miniLessons.unlocked.includes(lessonId)) return prev;
        return {
          ...prev,
          achievements: {
            ...a,
            miniLessons: {
              ...a.miniLessons,
              unlocked: [...a.miniLessons.unlocked, lessonId],
            },
          },
        };
      });
    },
    [setProgress],
  );

  const markMiniLessonRead = useCallback(
    (lessonId: string) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.miniLessons.readAt[lessonId]) return prev;
        return {
          ...prev,
          achievements: {
            ...a,
            miniLessons: {
              ...a.miniLessons,
              readAt: { ...a.miniLessons.readAt, [lessonId]: Date.now() },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const setMiniLessonReadingProgress = useCallback(
    (lessonId: string, pct: number) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        return {
          ...prev,
          achievements: {
            ...a,
            miniLessons: {
              ...a.miniLessons,
              readingProgress: { ...a.miniLessons.readingProgress, [lessonId]: pct },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const recordQuizPackResult = useCallback(
    (packId: string, scorePct: number, perQuestion: Record<string, boolean>) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        return {
          ...prev,
          achievements: {
            ...a,
            miniLessons: {
              ...a.miniLessons,
              quizPacksSolved: {
                ...a.miniLessons.quizPacksSolved,
                [packId]: { packId, solvedAt: Date.now(), scorePct, perQuestion },
              },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const recordPomodoroSession = useCallback(
    (startHour: number) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        // P3.3 (3 JUL · ADR-009): prune sessions older than 90d on write — the setter
        // comment already claimed this retention but the trim was missing.
        const cutoff = Date.now() - EVENT_RETENTION_MS;
        const kept = a.pomodoroSessions.filter((s) => s.at >= cutoff);
        const next = {
          ...prev,
          achievements: {
            ...a,
            pomodoroSessions: [...kept, { at: Date.now(), startHour }],
          },
        };
        return appendEvent(next, 'pomodoro.done', { startHour });
      });
    },
    [setProgress],
  );

  const addScreenTime = useCallback(
    (seconds: number, ticketId?: string | null) => {
      if (!Number.isFinite(seconds) || seconds <= 0) return;
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        const d = new Date();
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const byDay = a.activityByDay ?? {};
        const byTicket = a.activityByTicket ?? {};
        return {
          ...prev,
          achievements: {
            ...a,
            activityByDay: { ...byDay, [key]: (byDay[key] ?? 0) + Math.round(seconds) },
            // Второй срез (доска · 11 JUL 2026): реальное время на открытом тикете.
            ...(ticketId
              ? {
                  activityByTicket: {
                    ...byTicket,
                    [ticketId]: (byTicket[ticketId] ?? 0) + Math.round(seconds),
                  },
                }
              : {}),
          },
        };
      });
    },
    [setProgress],
  );

  const recordExamAchievementFlags = useCallback(
    (input: { bankId: string; pct: number; passed: boolean; attempt: number }) => {
      const { bankId, pct, passed, attempt } = input;
      // 'block1-exam' → 'block1' (короткий ключ для флагов).
      const short = bankId.replace('-exam', '');
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        const flags: Record<string, number> = { ...(a.capabilityFlags ?? {}) };
        if (passed) {
          const firstTimePass = flags[`exam-${short}-passed`] !== 1;
          flags[`exam-${short}-passed`] = 1;
          if (pct >= 90) flags[`exam-${short}-aced`] = 1;
          // счётчик различных сданных экзаменов (для бейджа «Все рубежи взяты»).
          if (firstTimePass) flags['exam-passed-count'] = (flags['exam-passed-count'] ?? 0) + 1;
          // «С первой попытки»: сдал с attempt 0 и без прошлого провала этого экзамена.
          if (attempt === 0 && flags[`exam-${short}-failed`] !== 1) flags['exam-first-try'] = 1;
          // «Работа над ошибками»: сдал после ранее зафиксированного провала.
          if (flags[`exam-${short}-failed`] === 1) flags['exam-comeback'] = 1;
        } else {
          flags[`exam-${short}-failed`] = 1;
        }
        return { ...prev, achievements: { ...a, capabilityFlags: flags } };
      });
    },
    [setProgress],
  );

  const recordThemeUsage = useCallback(
    (theme: string) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        const current = a.themeUsage[theme] ?? 0;
        return {
          ...prev,
          achievements: {
            ...a,
            themeUsage: { ...a.themeUsage, [theme]: current + 1 },
          },
        };
      });
    },
    [setProgress],
  );

  const recordLocaleUsage = useCallback(
    (locale: string) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        const current = a.localeUsage[locale] ?? 0;
        return {
          ...prev,
          achievements: {
            ...a,
            localeUsage: { ...a.localeUsage, [locale]: current + 1 },
          },
        };
      });
    },
    [setProgress],
  );

  const markSeniorExtensionOpened = useCallback(
    (key: string) => {
      setProgress((prev) => {
        const a = prev.achievements ?? defaultAchievements();
        if (a.seniorExtensionsOpened[key]) return prev;
        return {
          ...prev,
          achievements: {
            ...a,
            seniorExtensionsOpened: { ...a.seniorExtensionsOpened, [key]: true },
          },
        };
      });
    },
    [setProgress],
  );

  const ensureBlock1Capstone = useCallback((): CapstoneState => {
    const existing = progress.capstone['block-1'];
    if (existing) return existing;
    const fresh = defaultBlock1Capstone();
    setProgress((prev) => ({
      ...prev,
      capstone: { ...prev.capstone, 'block-1': fresh },
    }));
    return fresh;
  }, [progress.capstone, setProgress]);

  const setCapstoneArtifactSubmitted = useCallback(
    (artifactId: CapstoneArtifactId, submitted: boolean) => {
      setProgress((prev) => {
        const existing = prev.capstone['block-1'] ?? defaultBlock1Capstone();
        const artifact = existing.artifacts[artifactId];
        return {
          ...prev,
          capstone: {
            ...prev.capstone,
            'block-1': {
              ...existing,
              artifacts: {
                ...existing.artifacts,
                [artifactId]: {
                  ...artifact,
                  submitted,
                  submittedAt: submitted ? new Date().toISOString() : undefined,
                },
              },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const setCapstoneRubricRequired = useCallback(
    (id: CapstoneRubricRequiredId, value: boolean) => {
      setProgress((prev) => {
        const existing = prev.capstone['block-1'] ?? defaultBlock1Capstone();
        return {
          ...prev,
          capstone: {
            ...prev.capstone,
            'block-1': {
              ...existing,
              rubricRequired: { ...existing.rubricRequired, [id]: value },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const setCapstoneRubricBonus = useCallback(
    (id: CapstoneRubricBonusId, value: boolean) => {
      setProgress((prev) => {
        const existing = prev.capstone['block-1'] ?? defaultBlock1Capstone();
        return {
          ...prev,
          capstone: {
            ...prev.capstone,
            'block-1': {
              ...existing,
              rubricBonus: { ...existing.rubricBonus, [id]: value },
            },
          },
        };
      });
    },
    [setProgress],
  );

  const markCapstoneReleaseUnlocked = useCallback(() => {
    setProgress((prev) => {
      const existing = prev.capstone['block-1'] ?? defaultBlock1Capstone();
      if (existing.releaseUnlockedAt) return prev;
      return {
        ...prev,
        capstone: {
          ...prev.capstone,
          'block-1': {
            ...existing,
            releaseUnlockedAt: new Date().toISOString(),
          },
        },
      };
    });
  }, [setProgress]);

  const progressRef = useRef(progress);
  progressRef.current = progress;
  const applyServerState = useCallback(
    (next: Progress) => {
      // Hydrate just in case the server omitted newer branches (defensive ·
      // server keeps the canonical Progress shape, but `hydrateProgress`
      // handles edge cases the same way it does for stored cache).
      const hydrated = hydrateProgress(next);
      // Idempotency · skip the setProgress if the server snapshot is the
      // same as our current state. Without this, two simultaneously-open
      // sessions for the same user ping-pong: 412 → refetch → applyServerState
      // → useAchievements engine re-evaluates → setProgress → diff →
      // schedule → fire → 412 → ... burning thousands of requests per second.
      if (JSON.stringify(hydrated) === JSON.stringify(progressRef.current)) {
        return;
      }
      setProgress(hydrated);
    },
    [setProgress],
  );

  const resetProgress = useCallback(() => setProgress(defaultProgress()), [setProgress]);

  const value = useMemo<ProgressContextValue>(
    () => ({
      progress,
      setLast,
      setTicketStatus,
      setHomeworkStatus,
      patchHomework,
      setACChecked,
      setQuickCheck,
      setNote,
      setWizardStep,
      setInlineQc,
      setSprintProgress,
      setSprintReflection,
      setQuizProgress,
      setTicketBlockedReason,
      setTicketAssignees,
      addProgressEvent,
      markWelcomeSeen,
      markSprint0TourCompleted,
      setExamDate,
      setMockResult,
      setBlockExamResult,
      setFlashcardState,
      getTicket,
      recordEarnedBadge,
      setAchievementProgress,
      setAccentOverlay,
      unlockAccentOverlay,
      unlockMiniLesson,
      markMiniLessonRead,
      setMiniLessonReadingProgress,
      recordQuizPackResult,
      recordPomodoroSession,
      addScreenTime,
      recordExamAchievementFlags,
      recordThemeUsage,
      recordLocaleUsage,
      markSeniorExtensionOpened,
      ensureBlock1Capstone,
      setCapstoneArtifactSubmitted,
      setCapstoneRubricRequired,
      setCapstoneRubricBonus,
      markCapstoneReleaseUnlocked,
      applyServerState,
      resetProgress,
      demoMode,
      setDemoMode,
    }),
    [
      progress,
      demoMode,
      setDemoMode,
      setLast,
      setTicketStatus,
      setHomeworkStatus,
      patchHomework,
      setACChecked,
      setQuickCheck,
      setNote,
      setWizardStep,
      setInlineQc,
      setSprintProgress,
      setSprintReflection,
      setQuizProgress,
      setTicketBlockedReason,
      setTicketAssignees,
      addProgressEvent,
      markWelcomeSeen,
      markSprint0TourCompleted,
      setExamDate,
      setMockResult,
      setBlockExamResult,
      setFlashcardState,
      getTicket,
      recordEarnedBadge,
      setAchievementProgress,
      setAccentOverlay,
      unlockAccentOverlay,
      unlockMiniLesson,
      markMiniLessonRead,
      setMiniLessonReadingProgress,
      recordQuizPackResult,
 recordPomodoroSession,
      addScreenTime,
      recordExamAchievementFlags,
      recordThemeUsage,
      recordLocaleUsage,
      markSeniorExtensionOpened,
      ensureBlock1Capstone,
      setCapstoneArtifactSubmitted,
      setCapstoneRubricRequired,
      setCapstoneRubricBonus,
      markCapstoneReleaseUnlocked,
      applyServerState,
      resetProgress,
    ],
  );

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress(): ProgressContextValue {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within <ProgressProvider>');
  return ctx;
}
